import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, Activity, RefreshCw, Globe, ShieldCheck, ShieldOff,
} from 'lucide-react';
import {
  panelOnlines, listGuardOnline,
  type CharacterRow, type GuardOnlineRow,
} from '@/shared/services/adminService';
import { NATIONS_PT, formatClass } from './labels';
import { worldName } from '@/features/Notifications/worldNames';
import { DataTable } from '@/shared/components/ui/DataTable';
import { useTableSort } from '@/shared/hooks/useTableSort';

const POLL_MS = 10000;

type Tab = 'game' | 'guard';

// [2026-07-08] Forçar reinício + lockouts SAÍRAM desta tela — agora vivem só
// na tela do Guard (Sessões), com o mesmo modal que existia aqui.

export default function OnlinesPage() {
  const [tab, setTab] = useState<Tab>('game');
  const [gameItems, setGameItems] = useState<CharacterRow[]>([]);
  const [guardItems, setGuardItems] = useState<GuardOnlineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [game, guard] = await Promise.all([
        panelOnlines().catch(() => []),
        listGuardOnline(60, 500, true).catch(() => []),   // aba Onlines: só com char ativo
      ]);
      setGameItems(game);
      setGuardItems(guard);
      setError(null);
      setLastUpdate(new Date());
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar onlines');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(() => fetchData(true), POLL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  const items = tab === 'game' ? gameItems : guardItems;
  const totalOnline = items.length;
  // Nation 1 = Capella, Nation 2 = Procyon
  const capella = gameItems.filter((c) => c.nation === 1).length;
  const procyon = gameItems.filter((c) => c.nation === 2).length;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Activity className="text-[#00e1d7]" size={26} />
            Online
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {totalOnline.toLocaleString('pt-BR')} {tab === 'game' ? 'personagens in-game' : 'sessões no Guard'}
            {lastUpdate && <> · atualizado {lastUpdate.toLocaleTimeString('pt-BR')}</>}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                       hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-5 surface-card rounded-xl p-1 w-fit overflow-x-auto">
        <TabBtn active={tab === 'game'} onClick={() => setTab('game')}
          icon={<Activity size={14} />} label="Online In-Game" count={gameItems.length} />
        <TabBtn active={tab === 'guard'} onClick={() => setTab('guard')}
          icon={<ShieldCheck size={14} />} label="Online no Guard" count={guardItems.length} />
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Total" value={tab === 'game' ? gameItems.length : guardItems.length} color="#00e1d7" />
        <Stat label="Procyon" value={procyon} color="#3b82f6" />
        <Stat label="Capella" value={capella} color="#ef4444" />
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {tab === 'guard' ? (
        <GuardTable items={guardItems} loading={loading} />
      ) : (
        <GameTable items={gameItems} loading={loading} />
      )}
    </div>
  );
}

// ----------------------------------------------------------------- Tabelas

function GameTable({ items, loading }: { items: CharacterRow[]; loading: boolean }) {
  const { sorted, sort, toggle } = useTableSort(items, { key: 'lev', order: 'desc' });
  return (
    <DataTable<CharacterRow>
      columns={[
        { key: 'name', label: 'Personagem', width: '2fr', sortKey: 'name',
          cell: (c) => <p className="text-sm font-bold text-white truncate">{c.name || '—'}</p> },
        { key: 'class', label: 'Classe', width: '1.4fr', sortKey: 'class',
          cell: (c) => <span className="text-xs text-white/75 truncate">{formatClass(c.class)}</span> },
        { key: 'lev', label: 'Lv.', width: '60px', sortKey: 'lev',
          cell: (c) => <span className="text-sm font-bold text-white">{c.lev ?? '—'}</span> },
        { key: 'nation', label: 'Nação', width: '110px', sortKey: 'nation',
          cell: (c) => {
            const nation = c.nation ? NATIONS_PT[c.nation] : null;
            return nation ? (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border ${nation.classes}`}>
                <Globe size={10} /> {nation.name}
              </span>
            ) : '—';
          } },
        { key: 'alz', label: 'Alz', width: '1fr', sortKey: 'alz',
          cell: (c) => <span className="text-xs font-mono text-white/65">{(c.alz ?? 0).toLocaleString('pt-BR')}</span> },
        { key: 'world', label: 'World', width: '70px', align: 'center', sortKey: 'worldidx',
          cell: (c) => <span className="text-xs text-white/55">{c.worldidx ?? '—'}</span> },
        { key: 'channel', label: 'Canal', width: '70px', align: 'center', sortKey: 'channelidx',
          cell: (c) => <span className="text-xs text-white/55">{c.channelidx ?? '—'}</span> },
        { key: 'dll', label: 'Guard', width: '70px', align: 'center',
          cell: (c) => <DllBadge hooked={!!c.dllhooked} /> },
      ]}
      rows={sorted}
      sort={sort}
      onSort={toggle}
      rowKey={(c) => c.characteridx}
      rowHref={(c) => `/admin/character/${c.characteridx}`}
      loading={loading}
      empty="Ninguém online"
      mobileTitle={(c) => c.name || '—'}
      mobileSubtitle={(c) => `${formatClass(c.class)} · Lv ${c.lev ?? '—'}`}
    />
  );
}

function GuardTable({ items, loading }: { items: GuardOnlineRow[]; loading: boolean }) {
  const { sorted, sort, toggle } = useTableSort(items, { key: 'charName', order: 'asc' });
  return (
    <DataTable<GuardOnlineRow>
      columns={[
        { key: 'char', label: 'Char', width: '1.8fr', sortKey: 'charName',
          cell: (r) => r.charIdx ? (
            <Link to={`/admin/character/${r.charIdx}`} onClick={(e) => e.stopPropagation()}
                  className="min-w-0 hover:text-[#00e1d7] transition">
              <p className="text-sm font-bold text-white truncate">{r.charName || `Char ${r.charIdx}`}</p>
              <p className="text-[10px] text-white/45 font-mono">CharIdx {r.charIdx}</p>
            </Link>
          ) : <p className="text-sm font-bold text-white truncate">—</p> },
        { key: 'account', label: 'Conta', width: '1.4fr', sortKey: 'accountId',
          cell: (r) => (
            <Link to={`/admin/account/${r.userNum}`} onClick={(e) => e.stopPropagation()}
                  className="min-w-0 hover:text-[#00e1d7] transition">
              <p className="text-sm font-semibold text-white/85 truncate">{r.accountId || `User ${r.userNum}`}</p>
              <p className="text-[10px] text-white/45 font-mono">UserNum {r.userNum}</p>
            </Link>
          ) },
        { key: 'class', label: 'Classe', width: '1fr', sortKey: 'classId',
          cell: (r) => <span className="text-xs text-white/65">{r.classId != null ? formatClass(r.classId) : '—'}</span> },
        { key: 'map', label: 'Mapa', width: '1.4fr', sortKey: 'mapId',
          cell: (r) => (
            <span className="text-xs text-white/75 truncate">
              {r.mapId != null ? worldName(r.mapId) : '—'}
            </span>
          ) },
        { key: 'state', label: 'Estado', width: '120px', sortKey: 'state',
          cell: (r) => r.state
            ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300">{r.state}</span>
            : <span className="text-[11px] text-white/35">—</span> },
        { key: 'channel', label: 'Canal', width: '70px', align: 'center', sortKey: 'channel',
          cell: (r) => <span className="text-xs font-mono text-white/55">{r.channel ?? '—'}</span> },
        { key: 'dll', label: 'Guard', width: '70px', align: 'center',
          cell: (r) => <DllBadge hooked={r.connected ?? !!r.dllHooked} /> },
      ]}
      rows={sorted}
      sort={sort}
      onSort={toggle}
      rowKey={(r) => `${r.userNum}-${r.charIdx ?? 'x'}`}
      loading={loading}
      empty="Ninguém conectado no guard"
      mobileTitle={(r) => r.charName || `Char ${r.charIdx ?? '?'}`}
      mobileSubtitle={(r) => `${r.accountId || `User ${r.userNum}`} · ${r.mapId != null ? worldName(r.mapId) : '—'}`}
    />
  );
}

// ----------------------------------------------------------------- bits

function TabBtn({ active, onClick, icon, label, count }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-[#00e1d7]/15 text-[#00e1d7]'
          : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}
    >
      {icon} {label}
      <span className="text-[10px] text-white/45 font-mono">({count})</span>
    </button>
  );
}

function DllBadge({ hooked }: { hooked: boolean }) {
  if (hooked) {
    return (
      <span
        title="Liberty.dll ativa (heartbeat F3 < 5min)"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold
                   border bg-emerald-500/10 border-emerald-400/30 text-emerald-400"
      >
        <ShieldCheck size={10} /> ON
      </span>
    );
  }
  return (
    <span
      title="Sem heartbeat da liberty.dll nos últimos 5min — cliente sem DLL ou cheat"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold
                 border bg-red-500/10 border-red-400/30 text-red-400"
    >
      <ShieldOff size={10} /> OFF
    </span>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="surface-card rounded-xl p-4">
      <div className="flex items-center gap-2 text-[11px] text-white/55 uppercase tracking-wider font-bold mb-1">
        <span className="w-2 h-2 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
        {label}
      </div>
      <p className="text-2xl font-black text-white leading-none">{value.toLocaleString('pt-BR')}</p>
    </div>
  );
}
